<!-- The honest marker.

     A simulated suspension is indistinguishable from a real one by design —
     that is the entire point of the feature, and it is also its one danger. So
     while one is running, every screen carries a strip that says so, and the
     strip is the only way out that is reachable from every tab.

     DELIBERATELY NOT THE DEMO BANNER. That one is Ionic's warning colour,
     because "this is not your data" is a different claim from "this is not
     real"; two yellow strips would read as one repeated thing and the second
     would stop being read. This is the app's own austere idiom — carriage
     ground, one hairline, mono caps — which is what the rest of the app looks
     like when it is telling you something rather than warning you.

     NOT RED. `--la-signal` means a train is late and nothing else, and a
     simulator that borrowed it would be claiming lateness in the one colour
     the app has taught people to read as lateness.

     IT DOES NOT COVER ANYTHING. `ion-router-outlet` is `position: absolute;
     inset: 0`, so it does not flow below a sibling — it paints the page's own
     header at y=0, under this strip. App.vue offsets the outlet by the height
     measured here, so the board's first row and the tab bar are exactly where
     they were, on a page that is this much shorter. -->
<template>
  <div ref="el" class="sim-banner" role="status">
    <span class="sim-what">SIMULATED DISRUPTION</span>
    <span class="sim-dot" aria-hidden="true">·</span>
    <span class="sim-which">{{ simulationName }}</span>
    <button
      type="button"
      class="sim-stop"
      :aria-label="`Stop simulating ${simulationName}`"
      @click="stopSimulation"
    >STOP</button>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { simulationName, stopSimulation } from '../app-state.js';

const el = ref(null);
let observer = null;

/**
 * Publish this strip's height so the router outlet can start below it.
 *
 * Measured rather than hardcoded, for the reason the demo banner is: the name
 * of a scenario wraps to two lines on a narrow phone, and a hardcoded offset
 * would put the second line over the board's first departure.
 */
function measure() {
  const host = el.value;
  if (!host) return;
  document.documentElement.style.setProperty(
    '--la-sim-banner-h', `${Math.ceil(host.getBoundingClientRect().height)}px`,
  );
}

onMounted(() => {
  measure();
  if (typeof ResizeObserver === 'function') {
    observer = new ResizeObserver(measure);
    if (el.value) observer.observe(el.value);
  }
});

// The scenario can change under the strip without it unmounting — starting a
// second scenario while one is running replaces the first — and a longer name
// is a taller strip.
watch(simulationName, () => { measure(); });

onBeforeUnmount(() => {
  observer?.disconnect();
  document.documentElement.style.removeProperty('--la-sim-banner-h');
});
</script>

<style scoped>
.sim-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px 7px 18px;
  padding-top: calc(7px + env(safe-area-inset-top));
  background: var(--la-platform);
  border-bottom: 0.5px solid var(--la-rule);
  font-family: var(--la-font-mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  line-height: 1.4;
}

.sim-what {
  color: var(--la-enamel);
  white-space: nowrap;
}

.sim-dot { color: var(--la-rule); }

/* The one thing on the strip that is not shouted: the scenario's own name, in
   the sentence case it was written in, and the part allowed to wrap. */
.sim-which {
  flex: 1;
  min-width: 0;
  font-family: var(--la-font-body);
  font-size: 11px;
  letter-spacing: 0;
  color: var(--la-dim);
}

.sim-stop {
  flex: none;
  min-height: 28px;
  padding: 0 10px;
  background: transparent;
  border: 0.5px solid var(--la-rule);
  border-radius: 5px;
  color: var(--la-enamel);
  font-family: var(--la-font-mono);
  font-size: 11px;
  letter-spacing: 0.06em;
  cursor: pointer;
}

.sim-stop:focus-visible {
  outline: 2px solid var(--la-enamel);
  outline-offset: 2px;
}
</style>
